/**
 * Offline Detection Hook
 * Monitors online/offline status and provides network information
 */

import { useState, useEffect } from "react";
import { log } from "@/lib/log";

interface NetworkInformation extends EventTarget {
  downlink?: number;
  effectiveType?: "slow-2g" | "2g" | "3g" | "4g";
  rtt?: number;
  saveData?: boolean;
  addEventListener(type: "change", listener: () => void): void;
  removeEventListener(type: "change", listener: () => void): void;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

interface UseOfflineDetectionReturn {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType: string;
  wasOffline: boolean;
}

export function useOfflineDetection(): UseOfflineDetectionReturn {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  const [connectionType, setConnectionType] = useState("unknown");
  const [isSlowConnection, setIsSlowConnection] = useState(false);

  useEffect(() => {
    const updateOnlineStatus = () => {
      const online = navigator.onLine;

      if (!online && isOnline) {
        // Just went offline
        setWasOffline(true);
        log.debug("Network: Offline detected");
      } else if (online && !isOnline) {
        // Just came back online
        log.debug("Network: Back online");
        // Keep wasOffline true for a few seconds to show recovery message
        setTimeout(() => setWasOffline(false), 3000);
      }

      setIsOnline(online);
    };

    const updateConnectionInfo = () => {
      const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

      if (connection) {
        const effectiveType = connection.effectiveType || "unknown";
        setConnectionType(effectiveType);

        // Consider 2g and slow-2g as slow connections
        const isSlow = effectiveType === "slow-2g" || effectiveType === "2g";
        setIsSlowConnection(isSlow);

        if (isSlow) {
          log.debug(`Network: Slow connection detected (${effectiveType})`);
        }
      }
    };

    // Initial check
    updateOnlineStatus();
    updateConnectionInfo();

    // Add event listeners
    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    // Monitor connection changes
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    if (connection) {
      connection.addEventListener("change", updateConnectionInfo);
    }

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
      if (connection) {
        connection.removeEventListener("change", updateConnectionInfo);
      }
    };
  }, [isOnline]);

  return {
    isOnline,
    isSlowConnection,
    connectionType,
    wasOffline,
  };
}
